import { GlobalToken } from 'antd/es/theme/interface';
import type { DataNode } from 'antd/es/tree';
import { CEControl, OrgNode, OrgLevel } from 'store/ducks/Reporting/types';
import { renderOrgNode, renderControlNode } from './renderOrgNode';

const ORG_LEVELS: OrgLevel[] = [
  'group',
  'division',
  'region',
  'legal_entity',
  'business_unit'
];

const getOrgValue = (
  control: CEControl,
  level: OrgLevel
): { id: number; name: string } | null => {
  const map: Record<OrgLevel, { id: number; name: string } | null> = {
    group: control.group,
    division: control.division,
    region: control.region,
    legal_entity: control.legal_entity,
    business_unit: control.business_unit
  };
  return map[level];
};

const EMPTY: { id: number; name: string } = { id: -1, name: '-' };

export const buildOrgTree = (controls: CEControl[]): OrgNode[] => {
  const roots = new Map<string, OrgNode>();

  for (const control of controls) {
    const groupOrg = control.group ?? EMPTY;
    const rootKey = `group-${groupOrg.id}-${groupOrg.name}`;
    if (!roots.has(rootKey)) {
      roots.set(rootKey, {
        id: groupOrg.id,
        name: groupOrg.name,
        level: 'group',
        children: new Map(),
        processes: []
      });
    }

    let currentMap = roots.get(rootKey)!.children;
    let currentNode: OrgNode = roots.get(rootKey)!;

    for (const level of ORG_LEVELS.slice(1)) {
      const org = getOrgValue(control, level) ?? EMPTY;
      const key = `${level}-${org.id}-${org.name}`;
      if (!currentMap.has(key)) {
        currentMap.set(key, {
          id: org.id,
          name: org.name,
          level,
          children: new Map(),
          processes: []
        });
      }
      currentNode = currentMap.get(key)!;
      currentMap = currentNode.children;
    }

    currentNode.processes.push(control);
  }

  return Array.from(roots.values());
};

export const buildTreeData = (
  orgNodes: OrgNode[],
  token: GlobalToken
): DataNode[] => {
  const mapOrgNode = (node: OrgNode, keyPrefix: string): DataNode => {
    const key = `${keyPrefix}-${node.level}-${node.id}`;

    const childOrgNodes = Array.from(node.children.values()).map((child) =>
      mapOrgNode(child, key)
    );

    const controlNodes: DataNode[] = node.processes.map(
      (control: CEControl, idx: number) => ({
        key: `${key}-control-${control.id ?? idx}-${control.control_name}`,
        title: renderControlNode(control, token)
      })
    );

    return {
      key,
      title: renderOrgNode(node.name, node.level, token),
      children: [...childOrgNodes, ...controlNodes]
    };
  };

  return orgNodes.map((node) => mapOrgNode(node, 'root'));
};
